import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, ViewChild, inject, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PackListService } from '../../services/pack-list.service';
import { Alert, AlertService, AlertType, PaginatorFetcher, PaginatorFilters, TableComponent, TableConfig } from '@michelin/theme';
import { PackListDetails, PackListPdfRequest, PackListReport } from '../../models/pack-list.model';
import { map, Observable, finalize } from 'rxjs';
import { PaginatedPackListDetailsResponse } from '../../models/paginated-pack-list.model';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';
import { PackListPdfContext, PackListPdfExportService } from '../../services/pack-list-pdf-export.service';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';

@Component({
  selector: 'app-packlist-details',
  templateUrl: './packlist-details.component.html',
  styleUrl: './packlist-details.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PacklistDetailsComponent implements OnInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<PackListDetails>;
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private packListService = inject(PackListService);
  private dialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);
  public alertService = inject(AlertService);
  private renderer = inject(Renderer2);
  protected translationService = inject(TranslationService);
  public packListPdfExportService = inject(PackListPdfExportService);
  private featureflagsService = inject(FeatureflagsService);

  packListId: string;
  packListNum?: string;
  source?: string;
  destination?: string;
  notes?: string;
  pageSizeOptions = [10, 25, 50, 100];
  isPdfGenerating = false;
  isPdfExportEnabled = this.featureflagsService.getFeatureFlagValue('isPacklistPdfEnabled');

  columnNames: Record<string, string> = {
    received: 'Received',
    receiveDate: 'Received Date',
    itemNumber: 'Item Number',
    description: 'Description',
    customerCode: 'Customer Code',
    customerName: 'Customer Name',
    workOrderNumber: 'Work Order Number',
    crn: 'CRN',
    lineNumber: 'Line #',
    processCode: 'PC',
    width: 'Width',
    barcode: 'Barcode',
    make: 'Make',
    series: 'Series',
    size: 'Size',
    tread: 'Tread',
    plant: 'Plant',
    site: 'Site',
    currentPlant: 'Current Plant',
    receivedDateStr: 'Received Date',
    dueDateStr: 'Due Date',
    casingStatus: 'Casing Status',
    rejectCode: 'Reject Code',
    rejectDescription: 'Reject Description',
    scrapCode: 'Scrap Code',
    scrapDescription: 'Scrap Description',
    disposalChargeDescription: 'Disposal Charge Description',
    application: 'Application',
    axle: 'Axle',
    ntr: 'NTR',
    casingGrade: 'Grade',
    mspn: 'MSPN',
    dealerFinishedRetreadNumber: 'Finished Retread Number',
    invoiceNumber: 'Invoice Number',
    invoiceDateStr: 'Invoice Date'
  };

  exportDataFetcher = (): Observable<ExportResponse> => {
    return this.packListService.searchPackListDetails(this.packListId, 1, 10000).pipe(
      map((response: PaginatedPackListDetailsResponse) => ({
        data: response.data.packListDetails as Record<string, any>[]
      }))
    );
  };

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: 'PackList-' + (this.packListNum || 'export'),
      extraColumns: this.extraColumns,
      pdfExportCallback: this.isPdfExportEnabled ? () => this.exportToPdf() : undefined,
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }

  exportToPdf(): void {
    if (this.isPdfGenerating) {
      return;
    }

    this.isPdfGenerating = true;
    this.cdr.markForCheck();

    const request: PackListPdfRequest = { packListNum: this.packListNum ?? '' };
    this.packListService
      .getPackListDetails(request)
      .pipe(
        finalize(() => {
          this.isPdfGenerating = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (report: PackListReport) => {
          const finishedRetreadPartSummary = report.finishedRetreadPartSummary || [];
          const repairPartSummary = report.repairPartNumberSummary || [];
          const rejectPartSummary = report.rejectPartNumberSummary || [];
          const tableRows = report.tableRows || [];
          const packlist: PackListPdfContext = {
            packListNumber: report.packListNumber,
            userLocationCode: report.userLocationCode,
            destinationType: report.destinationType,
            destinationName: report.destinationName,
            sourceName: report.sourceName,
            sourceType: report.sourceType,
            userLogin: report.userLogin,
            generationDate: report.generationDate,
            totalCasings: report.totalCasings,
            retreadCount: report.retreadCount,
            rejectCount: report.rejectCount
          };
          this.packListPdfExportService.generatePackListPdf(
            packlist,
            finishedRetreadPartSummary,
            repairPartSummary,
            rejectPartSummary,
            tableRows
          );
        },
        error: () => {
          this.alertService.showAlert({
            type: AlertType.strong,
            content: this.translationService.translate('errorText')
          } as Alert);
        }
      });
  }

  tableConfig: TableConfig<PackListDetails> = {
    columns: {
      received: {
        cell: (item: PackListDetails) => item.received,
        header: this.translationService.translate('receivedText'),
        sortable: true
      },
      receiveDate: {
        cell: (item: PackListDetails) => item.receiveDate,
        header: this.translationService.translate('receivedDateText')
      },
      itemNumber: {
        cell: (item: PackListDetails) => item.itemNumber,
        header: this.translationService.translate('casingNumText')
      },
      description: {
        cell: (item: PackListDetails) => item.description,
        header: this.translationService.translate('descriptionText')
      },
      customerCode: {
        cell: (item: PackListDetails) => item.customerCode,
        header: this.translationService.translate('customerCodeText')
      },
      customerName: {
        cell: (item: PackListDetails) => item.customerName,
        header: this.translationService.translate('customerNameText')
      },
      workOrderNumber: {
        cell: (item: PackListDetails) => item.workOrderNumber,
        header: this.translationService.translate('workOrderNumberText'),
        sortable: true
      },
      crn: {
        cell: (item: PackListDetails) => item.crn,
        header: this.translationService.translate('CRNText')
      },
      lineNumber: {
        cell: (item: PackListDetails) => item.lineNumber,
        header: this.translationService.translate('lineNumText')
      },
      processCode: {
        cell: (item: PackListDetails) => item.processCode,
        header: this.translationService.translate('processCodeText')
      },
      width: {
        cell: (item: PackListDetails) => item.width,
        header: this.translationService.translate('widthText')
      }
    },
    paginator: {
      pageSize: 10,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom',
      pageLabelOptions: {
        firstPageLabel: '',
        lastPageLabel: '',
        nextPageLabel: '',
        previousPageLabel: '',
        itemsPerPageLabel: 'Items per page'
      }
    }
  };

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.packListNum = params['packListNum'];
      this.source = params['source'];
      this.destination = params['destination'];
      this.notes = params['notes'] ?? '';
    });
    this.route.params.subscribe((params) => {
      this.packListId = params['packListId'];
    });
  }

  backNavigation(): void {
    this.router.navigate(['home/packlists']);
  }

  get extraColumns(): Record<string, string> {
    return {
      'Pack List #': this.packListNum ?? '',
      'Source': this.source ?? '',
      'Destination': this.destination ?? '',
      'Notes': this.notes ?? ''
    };
  }

  fetcher: PaginatorFetcher<PackListDetails> = (filters: PaginatorFilters) => {
    let sortBy: string | undefined;
    let sortDir: string | undefined;
    if (filters.sort) {
      const [sortField, sortDirection] = filters.sort.split(':');
      if (sortField && sortDirection) {
        sortBy = sortField;
        sortDir = sortDirection;
      }
    }
    return this.packListService.searchPackListDetails(this.packListId, filters.page, filters.limit, sortBy, sortDir).pipe(
      map((response: PaginatedPackListDetailsResponse) => {
        if (response && response.error && response.error.length > 0) {
          this.alertService.showAlert({
            type: AlertType.strong,
            content: this.translationService.translate('tryAgainText')
          } as Alert);
          return;
        } else {
          const items: PackListDetails[] = response.data.packListDetails || [];
          const count = response.data.pagination.totalRecords || 0;
          this.addReceivedColumnTooltip();
          return { items, count };
        }
      })
    );
  };

  private addReceivedColumnTooltip(): void {
    try {
      const receivedHeader = document.querySelector('#received');
      if (receivedHeader) {
        this.renderer.setAttribute(receivedHeader, 'title', this.translationService.translate('indicatesCasingReceivedText'));
        this.renderer.setStyle(receivedHeader, 'cursor', 'help');
      } else {
        console.warn('Received column header element not found to add tooltip');
      }
    } catch (error) {
      console.error('Error adding tooltip to received column:', error);
    }
  }
}
